import * as ExcelJS from 'exceljs';
import * as Comlink from 'comlink';
import { saveAs } from 'file-saver';
import { formatExcelDate } from './excel';
import {
  ADVANCES_ANALYTICS_COLUMNS,
  BILLING_COLUMNS,
  CONSUMER_ANALYTICS_COLUMNS,
  CONSUMER_ECONOMICS_COLUMNS,
  CONSUMER_HUBSPOT_COLUMNS,
  DETAILED_SIGNUP_FUNNEL_COLUMNS,
  ENGAGEMENT_COLUMNS,
  GENERIC_SIGNUP_COLUMNS,
  LOGINS_COLUMNS,
  OUTSTANDING_ADVANCES_COLUMNS,
  REFERRALS_COLUMNS,
  REPAYMENTS_ANALYTICS_COLUMNS,
  REWARDS_COLUMNS,
  REWARDS_LOG_COLUMNS,
  RISK_INDICATORS_COLUMNS,
  SIGNUP_FUNNEL_COLUMNS,
  SURVEYS_COLUMNS,
  TRANSFERS_ANALYTICS_COLUMNS,
} from '../constants/reports';

export const processReport = (reportType, report) => {
  switch (reportType) {
    case 'ADVANCES_ANALYTICS': {
      return { processedReport: report.sort((a, b) => a.id - b.id) };
    }
    case 'CONSUMER_ECONOMICS': {
      const endRow = report.length + 1;

      const excelTotalRow = {
        consumerId: undefined,
        firstName: undefined,
        lastName: undefined,
        billableDays: undefined,
        periodRevenue: { formula: `SUM(E2:E${endRow})` },
        activeDays: { formula: `AVERAGE(F2:F${endRow})` },
        creditDays: { formula: `AVERAGE(G2:G${endRow})` },
        creditDaysPercent: { formula: `AVERAGE(H2:H${endRow})` },
        bridgePoints: { formula: `AVERAGE(I2:I${endRow})` },
        bridgePointsPerMonth: { formula: `AVERAGE(J2:J${endRow})` },
        averageDailyOustrandingCredit: { formula: `AVERAGE(K2:K${endRow})` },
        creditCost: { formula: `SUM(L2:L${endRow})` },
        creditAch: { formula: `SUM(M2:M${endRow})` },
        debitAch: { formula: `SUM(N2:N${endRow})` },
        successfulAch: { formula: `SUM(O2:O${endRow})` },
        failedAch: { formula: `SUM(P2:P${endRow})` },
        achCost: { formula: `SUM(Q2:Q${endRow})` },
        achFees: { formula: `SUM(R2:R${endRow})` },
        monitoringCost: { formula: `SUM(S2:S${endRow})` },
        estimatedDefaultCost: { formula: `SUM(T2:T${endRow})` },
        totalCost: { formula: `SUM(U2:U${endRow})` },
        totalMargin: { formula: `SUM(V2:V${endRow})` },
      };

      const totalRow = Object.entries(excelTotalRow).map(([key, value]) => {
        if (value) {
          const fieldValues = report.map(r => r[key]);

          if (value.formula.includes('SUM(') || value.formula.includes('AVERAGE(')) {
            return [
              key,
              {
                formula: value.formula,
                result:
                  fieldValues.reduce((a, b) => a + b, 0) /
                  (value.formula.includes('AVERAGE(') ? fieldValues.length : 1),
              },
            ];
          }
        }

        return [key, null];
      });

      return {
        totalRow,
        processedReport: [
          ...report,
          totalRow.reduce(
            (acc, [key, value]) => ({ ...acc, [key]: value ? value.result : null }),
            {},
          ),
        ],
      };
    }
    case 'LOGINS': {
      return {
        processedReport: report
          .sort((a, b) => a.timestamp - b.timestamp)
          .map(r => ({ ...r, uuid: crypto.randomUUID() })),
      };
    }
    case 'DETAILED_SIGNUP_FUNNEL': {
      const nextAccountEvents = [];

      const newReport = report.map((r, idx) =>
        Object.entries(r).reduce((acc, [key, value]) => {
          let newValue = value;

          if (key === 'ipLocation') {
            newValue =
              value !== null && (value.country || value.city)
                ? `${value.country} / ${value.city}`
                : null;
          } else if (key === 'accountEvents' && value.length > 0) {
            if (value.length > 1) {
              nextAccountEvents.push({
                idx,
                value: value.slice(1).map(x => ({ eventName: x.eventName, ...x.metadata })),
              });
            }

            return { ...acc, eventName: value[0].eventName, ...value[0].metadata };
          }

          return { ...acc, [key]: newValue };
        }, {}),
      );

      let nextAccountEventsCount = 0;

      nextAccountEvents.forEach(({ value, idx }) => {
        newReport.splice(
          idx + 1 + nextAccountEventsCount,
          0,
          ...value.map(x => ({ ...x, groupParentIndex: idx + nextAccountEventsCount })),
        );

        nextAccountEventsCount += value.length;
      });

      const processedReport = newReport.map(r => ({ ...r, uuid: crypto.randomUUID() }));

      const groups = [];

      processedReport.forEach((rep, childIndex) => {
        if (rep.groupParentIndex !== null && rep.groupParentIndex !== undefined) {
          const foundIndex = groups.findIndex(g => rep.groupParentIndex === g.parentIndex);

          if (foundIndex !== -1) {
            groups[foundIndex] = {
              parentIndex: rep.groupParentIndex,
              childIndices: [...groups[foundIndex].childIndices, childIndex],
            };
          } else {
            groups.push({ parentIndex: rep.groupParentIndex, childIndices: [childIndex] });
          }
        }
      });

      return { processedReport, groups };
    }
    case 'ENGAGEMENT': {
      return {
        processedReport: report
          .sort((a, b) => a.consumerId - b.consumerId)
          .map(x => ({
            ...x,
            billable: x.billable ? 'Yes' : 'No',
            signup: x.signup ? 'Yes' : 'No',
            engaged: x.engaged ? 'Yes' : 'No',
          })),
      };
    }
    case 'OUTSTANDING_ADVANCES': {
      return {
        processedReport: report.sort((a, b) => a.advanceId - b.advanceId),
      };
    }
    case 'REFERRALS': {
      return {
        processedReport: report
          .sort((a, b) => a.refereeId - b.refereeId)
          .sort((a, b) => a.referralId - b.referralId)
          .reduce((acc, val) => {
            if (acc.find(x => x.referralId === val.referralId)) {
              return [
                ...acc,
                {
                  refereeId: val.refereeId,
                  refereeFirstName: val.refereeFirstName,
                  refereeLastName: val.refereeLastName,
                  signupDate: val.signupDate,
                },
              ];
            }

            return [...acc, val];
          }, [])
          .map(r => ({ ...r, uuid: crypto.randomUUID() })),
      };
    }
    case 'REPAYMENTS_ANALYTICS': {
      return {
        processedReport: report.map(r => ({ ...r, uuid: crypto.randomUUID() })),
      };
    }
    case 'REWARDS': {
      return {
        processedReport: report.map(x =>
          x.category === 'VIDEO'
            ? {
                ...x,
                readCounter: Object.entries(x.watchList || {})
                  .sort(([, a], [, b]) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map(([, value]) => {
                    const minutes = Math.floor(value.duration / 60);
                    const seconds = value.duration - minutes * 60;

                    return `${minutes > 9 ? '' : '0'}${minutes}:${
                      seconds > 9 ? '' : '0'
                    }${seconds}`;
                  })
                  .join('\n'),
              }
            : x,
        ),
      };
    }
    case 'REWARDS_LOG': {
      return {
        processedReport: report.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
      };
    }
    case 'SIGNUP_FUNNEL': {
      const endRow = report.length + 1;

      const excelTotalRow = {
        aggId: undefined,
        aggName: undefined,
        openSignupLink: { formula: `SUM(C2:C${endRow})` },
        consumerCreated: { formula: `SUM(D2:D${endRow})` },
        accountLinkCompleted: { formula: `SUM(E2:E${endRow})` },
      };

      const totalRow = Object.entries(excelTotalRow).map(([key, value]) => {
        if (value) {
          const fieldValues = report.map(r => r[key]);

          if (value.formula.includes('SUM(')) {
            return [
              key,
              { formula: value.formula, result: fieldValues.reduce((a, b) => a + b, 0) },
            ];
          }
        }

        return [key, null];
      });

      return {
        totalRow,
        processedReport: [
          ...report,
          totalRow.reduce(
            (acc, [key, value]) => ({ ...acc, [key]: value ? value.result : null }),
            {},
          ),
        ],
      };
    }
    case 'SURVEYS': {
      return {
        processedReport: report.map(x => ({ ...x, uuid: crypto.randomUUID() })),
      };
    }
    case 'TRANSFERS_ANALYTICS': {
      return {
        processedReport: report.sort((a, b) => new Date(a.createTime) - new Date(b.createTime)),
      };
    }
    default: {
      return { processedReport: report };
    }
  }
};

export const exportExcelFile = async (workbook, fileName) => {
  const data = await workbook.xlsx.writeBuffer();
  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
  });

  saveAs(blob, fileName);
};

const getReportExportConfig = (reportType, report) => {
  const { processedReport, totalRow, groups } = processReport(reportType, report);
  const endRow = processedReport.length + 1;

  switch (reportType) {
    case 'ADVANCES_ANALYTICS': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Advances Analytics',
        tableColumns: ADVANCES_ANALYTICS_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          openTime: r.openTime ? formatExcelDate(new Date(r.openTime)) : null,
          closeTime: r.closeTime ? formatExcelDate(new Date(r.closeTime)) : null,
        })),
      };
    }
    case 'AGGREGATOR_MONTHLY': {
      return {
        exportFileType: 'custom',
        exportFile: async () => {
          const worker = new Worker(new URL('../web-workers/pdfWorker.js', import.meta.url));
          const pdfWorker = Comlink.wrap(worker);

          const blob = await pdfWorker.generateAggregatorMonthlyReport(report);

          saveAs(blob, 'aggregator-monthly.zip');
        },
      };
    }
    case 'BILLING': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Billing',
        tableColumns: BILLING_COLUMNS,
        formattedReport: processedReport,
      };
    }
    case 'CONSUMER_ANALYTICS': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Consumer Analytics',
        tableColumns: CONSUMER_ANALYTICS_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          registrationTime: r.registrationTime
            ? formatExcelDate(new Date(r.registrationTime))
            : null,
          dateOfBirth: r.dateOfBirth ? formatExcelDate(new Date(r.dateOfBirth)) : null,
          firstLogin: r.firstLogin ? formatExcelDate(new Date(r.firstLogin)) : null,
          lastLogin: r.lastLogin ? formatExcelDate(new Date(r.lastLogin)) : null,
          nextPayroll: r.nextPayroll ? formatExcelDate(new Date(r.nextPayroll)) : null,
        })),
        conditionalFormatting: {
          ref: `AA2:AA${endRow}`,
          rules: [
            {
              type: 'expression',
              formulae: [`ISTEXT(INDEX($A$1:$AB$${endRow}, ROW(), COLUMN()))`],
              style: { alignment: { horizontal: 'left' } },
            },
          ],
        },
      };
    }
    case 'CONSUMER_ECONOMICS': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Consumer Economics',
        tableColumns: CONSUMER_ECONOMICS_COLUMNS,
        // NOTE: using 'report' instead of 'processedReport' intentionally
        formattedReport: report,
        totalRow,
      };
    }
    case 'CONSUMER_HUBSPOT': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Consumer HubSpot',
        tableColumns: CONSUMER_HUBSPOT_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          dateOfBirth: r.dateOfBirth ? formatExcelDate(new Date(r.dateOfBirth)) : null,
          registrationDate: r.registrationDate
            ? formatExcelDate(new Date(r.registrationDate))
            : null,
        })),
      };
    }
    case 'LOGINS': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Consumer Logins',
        tableColumns: LOGINS_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          timestamp: r.timestamp ? formatExcelDate(new Date(r.timestamp)) : null,
        })),
      };
    }
    case 'DETAILED_SIGNUP_FUNNEL': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Detailed Signup Funnel',
        tableColumns: DETAILED_SIGNUP_FUNNEL_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          estTime: r.estTime ? formatExcelDate(new Date(r.estTime)) : null,
          timestamp: r.timestamp ? formatExcelDate(new Date(r.timestamp)) : null,
        })),
        setCustomHeaderRow: headerRow => {
          headerRow.eachCell((cell, colNumber) => {
            // eslint-disable-next-line no-param-reassign
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: colNumber >= 14 ? 'DADADA' : 'FFFF00' },
            };
          });
        },
        groups,
        conditionalFormatting: {
          ref: `H2:M${endRow}`,
          rules: [
            {
              type: 'expression',
              formulae: [`IF(INDEX($A$1:$M$${endRow}, ROW(), COLUMN()) = "ok", TRUE, FALSE)`],
              style: {
                fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FF008000' } },
                font: { color: { argb: 'FF008000' } },
              },
            },
            {
              type: 'expression',
              formulae: [
                `IF(AND(ISTEXT(INDEX($A$1:$M$${endRow}, ROW(), COLUMN())), ` +
                  `INDEX($A$1:$M$${endRow}, ROW(), COLUMN()) <> "ok"), TRUE, FALSE)`,
              ],
              style: {
                fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFF0000' } },
                font: { color: { argb: 'FFFFFFFF' } },
              },
            },
          ],
        },
      };
    }
    case 'ENGAGEMENT': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Consumers Engagement',
        tableColumns: ENGAGEMENT_COLUMNS,
        formattedReport: processedReport,
      };
    }
    case 'GENERIC_SIGNUP': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Generic Signup',
        tableColumns: GENERIC_SIGNUP_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          timestamp: r.timestamp ? formatExcelDate(new Date(r.timestamp)) : null,
          consent: r.consent ? 'Yes' : 'No',
        })),
      };
    }
    case 'OUTSTANDING_ADVANCES': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Outstanding Advances',
        tableColumns: OUTSTANDING_ADVANCES_COLUMNS,
        formattedReport: processedReport,
      };
    }
    case 'REFERRALS': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Referrals',
        tableColumns: REFERRALS_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          signupDate: r.signupDate ? formatExcelDate(new Date(r.signupDate)) : null,
        })),
      };
    }
    case 'REPAYMENTS_ANALYTICS': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Repayment Analytics',
        tableColumns: REPAYMENTS_ANALYTICS_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          trialTimestamp: r.trialTimestamp ? formatExcelDate(new Date(r.trialTimestamp)) : null,
        })),
        setCustomHeaderRow: headerRow => {
          headerRow.eachCell((cell, colNumber) => {
            // eslint-disable-next-line no-param-reassign
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: colNumber >= 16 ? 'f3a83c' : 'FFFF00' },
            };
          });
        },
      };
    }
    case 'REWARDS': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Rewards',
        tableColumns: REWARDS_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          offerDate: r.offerDate ? formatExcelDate(new Date(r.offerDate)) : null,
          subscriptionDate: r.subscriptionDate
            ? formatExcelDate(new Date(r.subscriptionDate))
            : null,
          closeDate: r.closeDate ? formatExcelDate(new Date(r.closeDate)) : null,
        })),
      };
    }
    case 'REWARDS_LOG': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Rewards Log',
        tableColumns: REWARDS_LOG_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          timestamp: r.timestamp ? formatExcelDate(new Date(r.timestamp)) : null,
        })),
      };
    }
    case 'RISK_INDICATORS': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Risk Indicators',
        tableColumns: RISK_INDICATORS_COLUMNS,
        formattedReport: processedReport,
        conditionalFormatting: {
          ref: `F2:O${endRow}`,
          rules: [
            {
              type: 'expression',
              formulae: [
                `IF(ISBLANK(INDEX($A$1:$Y$${endRow}, ROW(), COLUMN() + 10)), FALSE,` +
                  ` INDEX($A$1:$Y$${endRow}, ROW(), COLUMN()) >` +
                  ` INDEX($A$1:$Y$${endRow}, ROW(), COLUMN() + 10))`,
              ],
              style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFF0000' } } },
            },
          ],
        },
      };
    }
    case 'SIGNUP_FUNNEL': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Signup Funnel',
        tableColumns: SIGNUP_FUNNEL_COLUMNS,
        // NOTE: using 'report' instead of 'processedReport' intentionally
        formattedReport: report,
        totalRow,
      };
    }
    case 'SURVEYS': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Surveys',
        tableColumns: SURVEYS_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          date: r.date ? formatExcelDate(new Date(r.date)) : null,
        })),
      };
    }
    case 'TRANSFERS_ANALYTICS': {
      return {
        exportFileType: 'excel',
        sheetTitle: 'Transfer Analytics',
        tableColumns: TRANSFERS_ANALYTICS_COLUMNS,
        formattedReport: processedReport.map(r => ({
          ...r,
          createTime: r.createTime ? formatExcelDate(new Date(r.createTime)) : null,
          confirmTime: r.confirmTime ? formatExcelDate(new Date(r.confirmTime)) : null,
          failTime: r.failTime ? formatExcelDate(new Date(r.failTime)) : null,
          reconciled: r.reconciled ? 'Yes' : 'No',
        })),
      };
    }

    default:
      return null;
  }
};

export const exportReportFile = async ({
  reportType,
  reportFileName,
  report,
  reportFormat = 'DATA',
}) => {
  if (reportFormat === 'FILE') {
    saveAs(report, `${reportFileName}.xlsx`);

    return;
  }

  const config = getReportExportConfig(reportType, report);

  if (!config) {
    return;
  }

  if (config.exportFileType === 'custom' && config.exportFile) {
    await config.exportFile();
  }

  if (config.exportFileType === 'excel') {
    const workbook = new ExcelJS.Workbook();

    const worksheet = workbook.addWorksheet(config.sheetTitle, {
      views: [{ state: 'frozen', ySplit: 1 }],
    });
    const wsColumns = Object.entries(config.tableColumns).map(([key, value]) => ({
      ...value,
      key,
    }));

    worksheet.columns = wsColumns;

    worksheet.addRows(config.formattedReport);

    const headerRow = worksheet.getRow(1);

    headerRow.alignment = { horizontal: 'left' };
    headerRow.font = { bold: true };

    if (config.setCustomHeaderRow) {
      config.setCustomHeaderRow(headerRow);
    } else {
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF00' } };
    }

    if (config.totalRow) {
      const footerRow = worksheet.addRow(config.totalRow.map(r => r[1]));

      footerRow.font = { bold: true };
    }

    if (config.groups) {
      config.groups.forEach(group => {
        group.childIndices.forEach(idx => {
          worksheet.getRow(idx + 2).outlineLevel = 1;
        });
      });

      worksheet.properties.outlineProperties = { summaryBelow: false, summaryRight: false };
    }

    if (config.conditionalFormatting) {
      worksheet.addConditionalFormatting(config.conditionalFormatting);
    }

    await exportExcelFile(workbook, `${reportFileName}.xlsx`);
  }
};
